import { signInWithPopup } from 'firebase/auth';
import { auth, googleProvider } from './firebaseConfig';
import { authService, UserInfo } from './authService';

export class GoogleAuthService {
  /**
   * Sign in with Google using Firebase popup
   */
  async signInWithGoogle(): Promise<UserInfo> {
    try {
      // Check if we're in test mode
      const isTestMode = import.meta.env.VITE_TEST_MODE === 'true';
      
      let result;
      if (isTestMode) {
        // Mock the sign-in for test mode
        result = {
          user: {
            uid: 'test-user-id',
            email: 'test@example.com',
            displayName: 'Test User',
            getIdToken: async () => 'test-firebase-token'
          }
        };
      } else {
        // Sign in with Google popup
        // Note: COOP warnings may appear in console but authentication should still work
        // The nginx config uses "unsafe-none" for COOP to allow OAuth popups
        result = await signInWithPopup(auth, googleProvider);
      }
      
      const user = result.user;
      
      // Get Firebase ID token
      const firebaseToken = await user.getIdToken();
      
      // Call backend authentication with Firebase token
      const userInfo = await authService.loginWithFirebase({
        firebase_token: firebaseToken
      });
      
      return userInfo;
    } catch (error: any) {
      console.error('Google authentication error:', error);
      
      // Handle specific error cases
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Sign-in was cancelled. Please try again.');
      } else if (error.code === 'auth/popup-blocked') {
        throw new Error('Popup was blocked by your browser. Please allow popups for this site.');
      } else if (error.code === 'auth/network-request-failed') {
        throw new Error('Network error. Please check your internet connection.');
      } else if (error.code === 'auth/too-many-requests') {
        throw new Error('Too many failed attempts. Please try again later.');
      } else {
        throw new Error(`Google authentication failed: ${error.message}`);
      }
    }
  }

  /**
   * Sign out from Google
   */
  async signOut(): Promise<void> {
    try {
      await auth.signOut();
    } catch (error: any) {
      console.error('Google sign out error:', error);
      throw new Error(`Sign out failed: ${error.message}`);
    }
  }

  /**
   * Get current Google user
   */
  getCurrentUser() {
    return auth.currentUser;
  }

  /**
   * Check if user is signed in with Google
   */
  isSignedIn(): boolean {
    return auth.currentUser !== null;
  }
}

// Create and export a single instance
export const googleAuthService = new GoogleAuthService();
